import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import NightsStayRounded from "@mui/icons-material/NightsStayRounded";
import WbSunnyRounded from "@mui/icons-material/WbSunnyRounded";

import { getMarketState, formatAgo } from "@/lib/market-status";
import { useRefreshBus } from "@/store/refresh-bus-store";

interface IdleConfirmDialogProps {
  open: boolean;
  /** Milliseconds since user's last interaction at the moment the modal
   *  opens. Used for the "You've been idle for about N minutes" copy. */
  idleMs: number;
  /** Timestamp of the last successful data refresh, for the "last
   *  updated" line. Undefined hides that line. */
  lastUpdate?: Date;
  /** Called when user picks "Refresh". Dialog shows a spinner until
   *  `open` flips to false or the caller resolves the returned
   *  promise. */
  onRefresh: () => void | Promise<void>;
  /** Called when user picks "Continue without refresh". Dialog closes
   *  immediately and caller is responsible for whatever book-keeping
   *  (e.g. marking the hero as stale-acknowledged). */
  onDismiss: () => void;
}

/**
 * Idle-confirm modal — Fidelity-pattern "Session status check". Shows
 * when the user has been inactive on a focused tab past the hard-idle
 * threshold (see `docs/REFRESH-POLICY.md`). Partially blocks the UI
 * until the user chooses; there is no close-X.
 *
 * The modal itself doesn't track engagement or trigger refreshes — it
 * just renders. The parent sentinel decides when `open` flips.
 */
export function IdleConfirmDialog({
  open,
  idleMs,
  lastUpdate,
  onRefresh,
  onDismiss
}: IdleConfirmDialogProps) {
  const [refreshing, setRefreshing] = useState(false);
  const clearStale = useRefreshBus((s) => s.clearStaleAcknowledged);

  // Reset local "refreshing" state whenever the dialog closes — the
  // next time it opens, the button should be interactive.
  useEffect(() => {
    if (!open) setRefreshing(false);
  }, [open]);

  const idleMinutes = Math.max(1, Math.round(idleMs / 60_000));
  const market = getMarketState();
  const marketLabel = market === "open" ? "Market open" : "Market closed";
  const MarketIcon = market === "open" ? WbSunnyRounded : NightsStayRounded;

  const handleRefreshClick = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
      // Even if the caller's refresh is async and the ack clear
      // happens on token bump upstream, make sure the flag is gone
      // by the time the dialog closes. Defensive.
      clearStale();
    } finally {
      // Don't flip refreshing=false here — parent closes the dialog
      // which effect above resets state. If parent leaves it open
      // (refresh failed), they should toast and re-enable by
      // re-rendering this component.
    }
  };

  return (
    <Dialog
      open={open}
      // Partial block — user must pick Refresh or Continue. No
      // escape-to-close, no backdrop-click-to-close.
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
      }}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>You've been idle</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No activity for about {idleMinutes} minute{idleMinutes === 1 ? "" : "s"}.
          The numbers you see may be out of date. Refresh to continue, or keep
          viewing the cached data.
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
          <MarketIcon
            sx={{ fontSize: 20, color: market === "open" ? "warning.main" : "text.secondary" }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {marketLabel}
          </Typography>
        </Stack>
        {lastUpdate && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, fontFeatureSettings: '"tnum" 1' }}
          >
            Last updated {formatAgo(lastUpdate)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onDismiss} color="inherit" size="small">
          Continue without refresh
        </Button>
        <Button
          onClick={handleRefreshClick}
          variant="contained"
          disabled={refreshing}
          startIcon={
            refreshing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <RefreshRounded />
            )
          }
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
